
import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pb } from '@/lib/pocketbase';
import { useSubjects } from '@/hooks/useSubjects';
import { Book, Dumbbell, Code, MapPin } from 'lucide-react';

interface SubjectSelectorProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  placeholder?: string;
}

const SubjectSelector = ({ value, onValueChange, placeholder = "Select subject..." }: SubjectSelectorProps) => {
  const { subjects, isLoading } = useSubjects();
  const [approvedSubjects, setApprovedSubjects] = useState<string[]>([]);
  const [tutorSubjects, setTutorSubjects] = useState<string[]>([]);

  // Fetch subjects from tutors
  const fetchTutorSubjects = async () => {
    try {
      const tutors = await pb.collection('tutors').getList(1, 500, {
        fields: 'subjects',
        requestKey: null
      });
      
      const allTutorSubjects = new Set<string>();
      tutors.items.forEach(tutor => {
        if (tutor.subjects && Array.isArray(tutor.subjects)) {
          tutor.subjects.forEach((subject: string) => {
            if (subject && subject.trim()) {
              allTutorSubjects.add(subject.trim());
            }
          });
        }
      });
      
      setTutorSubjects(Array.from(allTutorSubjects));
    } catch (error) {
      setTutorSubjects([]);
    }
  };

  // Fetch approved subjects (keeping existing functionality for backward compatibility)
  const fetchApprovedSubjects = async () => {
    try {
      // Fetch from approved_subjects collection
      const approvedRecords = await pb.collection('approved_subjects').getList(1, 100, {
        filter: 'isActive = true',
        sort: '-created'
      });
      
      // Fetch from subject_requests collection (approved requests)
      const subjectRequestsRecords = await pb.collection('subject_requests').getList(1, 100, {
        filter: 'status = "approved" & isActive = true',
        sort: '-created'
      });
      
      // Combine all approved subjects
      const approvedSubjectNames = approvedRecords.items.map(item => item.name);
      const approvedRequestSubjectNames = subjectRequestsRecords.items.map(item => item.subjectName);
      
      // Remove duplicates
      const allApprovedSubjects = [...new Set([...approvedSubjectNames, ...approvedRequestSubjectNames])];
      
      setApprovedSubjects(allApprovedSubjects);
    } catch (error) {
      setApprovedSubjects([]);
    }
  };

  useEffect(() => {
    fetchApprovedSubjects();
    fetchTutorSubjects();
    
    // Set up real-time subscriptions for both approved subjects collections
    let unsubscribeApproved: (() => void) | undefined;
    let unsubscribeRequests: (() => void) | undefined;
    
    const setupRealtimeSubscriptions = async () => {
      try {
        // Subscribe to approved_subjects collection
        unsubscribeApproved = await pb.collection('approved_subjects').subscribe('*', (e) => {
          setTimeout(() => {
            fetchApprovedSubjects();
          }, 500);
        });
        
        // Subscribe to subject_requests collection
        unsubscribeRequests = await pb.collection('subject_requests').subscribe('*', (e) => {
          setTimeout(() => {
            fetchApprovedSubjects();
          }, 500);
        });
        
      } catch (error) {
        // Silently handle subscription setup errors
      }
    };
    
    setupRealtimeSubscriptions();
    
    return () => {
      if (unsubscribeApproved) {
        try {
          unsubscribeApproved();
        } catch (error) {
          // Silently handle unsubscribe errors
        }
      }
      if (unsubscribeRequests) {
        try {
          unsubscribeRequests();
        } catch (error) {
          // Silently handle unsubscribe errors
        }
      }
    };
  }, []);

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === 'all') {
      onValueChange(null);
    } else {
      onValueChange(selectedValue);
    }
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Book': return Book;
      case 'Dumbbell': return Dumbbell;
      case 'Code': return Code;
      case 'MapPin': return MapPin;
      default: return Book;
    }
  };

  // Separate subjects by category
  const focusSubjects = subjects.filter(s => s.category === 'focus');
  const defaultSubjects = subjects.filter(s => s.category === 'default');
  const databaseApprovedSubjects = subjects.filter(s => s.category === 'approved');

  // Filter out duplicates between approved_subjects collection and subjects collection
  const uniqueApprovedSubjects = approvedSubjects.filter(
    subject => !databaseApprovedSubjects.some(dbSubject => dbSubject.name === subject)
  );

  // Get all existing subject names to filter duplicates from tutor subjects
  const existingSubjectNames = new Set([
    ...defaultSubjects.map(s => s.name),
    ...focusSubjects.map(s => s.name),
    ...databaseApprovedSubjects.map(s => s.name),
    ...uniqueApprovedSubjects
  ]);

  // Filter tutor subjects to remove duplicates
  const uniqueTutorSubjects = tutorSubjects
    .filter(subject => !existingSubjectNames.has(subject))
    .sort();

  return (
    <Select value={value || 'all'} onValueChange={handleValueChange}>
      <SelectTrigger className="h-12 text-lg border-2 border-gray-200 focus:border-blue-500 transition-colors">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-80 overflow-y-auto bg-white z-50">
        <SelectItem value="all" className="font-medium">
          All Subjects
        </SelectItem>
        
        {/* Default subjects first */}
        {defaultSubjects.map((subject, index) => {
          const IconComponent = getIconComponent(subject.icon || 'Book');
          return (
            <SelectItem key={`default-${subject.id}-${index}`} value={subject.name} className="py-2">
              <div className="flex items-center gap-2 w-full">
                <IconComponent className="h-4 w-4 flex-shrink-0 text-gray-500" />
                <span>{subject.name}</span>
              </div>
            </SelectItem>
          );
        })}
        
        {/* Focus subjects second with icons */}
        {focusSubjects.map((subject, index) => {
          const IconComponent = getIconComponent(subject.icon || 'Book');
          return (
            <SelectItem key={`focus-${subject.id}-${index}`} value={subject.name} className="py-3">
              <div className="flex items-center gap-3 w-full">
                <IconComponent className="h-5 w-5 flex-shrink-0 text-blue-600" />
                <span className="font-semibold text-blue-700 text-base">{subject.name}</span>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full ml-auto">Focus</span>
              </div>
            </SelectItem>
          );
        })}
        
        {/* Database approved subjects */}
        {databaseApprovedSubjects.map((subject, index) => {
          const IconComponent = getIconComponent(subject.icon || 'Book');
          return (
            <SelectItem key={`db-approved-${subject.id}-${index}`} value={subject.name} className="py-2">
              <div className="flex items-center gap-2 w-full">
                <IconComponent className="h-4 w-4 flex-shrink-0 text-green-600" />
                <span className="text-green-700 font-medium">{subject.name}</span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full ml-auto">Approved</span>
              </div>
            </SelectItem>
          );
        })}
        
        {/* Legacy approved subjects from approved_subjects collection */}
        {uniqueApprovedSubjects.map((subject, index) => (
          <SelectItem key={`legacy-approved-${subject}-${index}`} value={subject} className="py-2">
            <div className="flex items-center gap-2 w-full">
              <Book className="h-4 w-4 flex-shrink-0 text-green-600" />
              <span className="text-green-700 font-medium">{subject}</span>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full ml-auto">New</span>
            </div>
          </SelectItem>
        ))}

        {/* Tutor subjects (subjects being taught by tutors) */}
        {uniqueTutorSubjects.map((subject, index) => (
          <SelectItem key={`tutor-${subject}-${index}`} value={subject} className="py-2">
            <div className="flex items-center gap-2 w-full">
              <Book className="h-4 w-4 flex-shrink-0 text-blue-500" />
              <span className="text-gray-800">{subject}</span>
              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full ml-auto">Available</span>
            </div>
          </SelectItem>
        ))}
          
        {/* Loading indicator */}
        {isLoading && subjects.length === 0 && (
          <SelectItem value="loading" disabled className="py-2">
            <div className="flex items-center gap-2 w-full">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
              <span className="text-gray-500">Loading subjects...</span>
            </div>
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
};

export default SubjectSelector;
